import { useEffect, useRef, useState, useContext } from 'react';
import UserMenu from '../Elements/UserMenu';
import HomeData from './home/HomeData';
import Workers from './worker/Workers';
import SettingsLayout from '../settings/SettingsLayout';
import CoinDropdown from '../Elements/CoinDropDown';
import { CoinContext, CoinProvider } from '../../context/CoinContext';
import Switcher from '../auth/Switcher';
import { appContext } from '../../App';
import { faArrowLeft, faChevronRight, faGem, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

function WorkerData() {
    const [activeTab, setActiveTab] = useState('DASHBOARD');
    const [settingsActive, setSettingsActive] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { darkMode, setDarkMode } = useContext(appContext);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const coinContext = useContext(CoinContext);
    const { authList } = useContext(appContext);


    if (!coinContext) {
        throw new Error('CoinContext must be used within a CoinProvider');
    }

    const { selectedCoin, setSelectedCoin } = coinContext;

    const currencyOptions = [
        { theme: 'bitcoin', code: 'BTC', symbol: 'BTC', image: '/assets/currency-btc.png' },
        { theme: 'kaspa', code: 'KAS', symbol: 'KAS', image: '/assets/currency-kas.png' },
        { theme: 'Alephium', code: 'ALPH', symbol: 'ALPH', image: '/assets/currency-alph.png' },
    ];

    useEffect(() => {
        if (settingsActive) {
            setActiveTab('SETTINGS');
        }
    }, [settingsActive]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const resetToHome = () => {
        setActiveTab('DASHBOARD');
        setSettingsActive(false);
    };

    const [settingsTab, setSettingsTab] = useState<'accounts' | 'payout' | 'security-2fa' | 'security-password'>('accounts');

    const NavigateToWallet = () => {
        setSettingsActive(true);
        setSettingsTab('payout');
    }

    const toggleCoinDropdown = () => {
        setIsDropdownOpen((prev) => !prev);
    };

    const handleCoinSelect = (code: string) => {
        setSelectedCoin(code);
        setIsDropdownOpen(false);
    };

    return (

        <div className="flex flex-col min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
            {/* Modern Dark Navbar */}
            <nav className="sticky top-0 z-50 bg-[#0c2a4a] dark:bg-slate-900 shadow-xl border-b border-white/10 dark:border-slate-700/50 backdrop-blur-md">
                <div className="container mx-auto px-3 sm:px-4 py-2.5">
                    {/* Main Navbar Row */}
                    <div className="flex items-center justify-between">
                        {/* Logo Section */}
                        <div className="flex items-center gap-3 md:gap-4">
                            <div className="flex items-center gap-3">
                                <div className="relative group">
                                    <img
                                        src="/assets/segpool.png"
                                        className="h-7 md:h-9 lg:h-10 cursor-pointer transition-all duration-300 group-hover:scale-105 group-hover:opacity-90"
                                        alt="Logo"
                                        onClick={resetToHome}
                                    />
                                    <div className="absolute -inset-2 bg-blue-500/10 rounded-lg blur-sm group-hover:opacity-100 opacity-0 transition-opacity"></div>
                                </div>
                            </div>
                            <button
                                className="sm:hidden p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {isMobileMenuOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                        </div>

                        {/* Mobile Menu Button */}


                        {/* Right Controls - Desktop */}
                        <div className="hidden sm:flex items-center gap-2 lg:gap-3">
                            {/* Theme Toggle */}
                            <button
                                onClick={() => setDarkMode(!darkMode)}
                                className="p-2.5 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 group"
                                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                            >
                                <FontAwesomeIcon
                                    icon={darkMode ? faSun : faMoon}
                                    className="text-lg group-hover:scale-110 transition-transform"
                                />
                            </button>

                            {/* Switcher */}
                            <div className="hidden md:block">
                                <Switcher />
                            </div>

                            {/* Coin Dropdown */}
                            <div className="relative">
                                <CoinDropdown
                                    selectedCoin={selectedCoin}
                                    currencyOptions={currencyOptions}
                                    onSelect={handleCoinSelect}
                                />
                            </div>

                            {/* User Menu */}
                            <div className="relative">
                                <UserMenu
                                    settingsActive={settingsActive}
                                    setSettingsActive={setSettingsActive}
                                />
                            </div>
                        </div>

                        {/* Mobile Right Controls */}
                        <div className="flex sm:hidden items-center gap-2">
                            <CoinDropdown
                                selectedCoin={selectedCoin}
                                currencyOptions={currencyOptions}
                                onSelect={handleCoinSelect}
                            />
                            <UserMenu
                                settingsActive={settingsActive}
                                setSettingsActive={setSettingsActive}
                            />
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="sm:hidden absolute top-full left-0 right-0 bg-[#0c2a4a] dark:bg-slate-900 shadow-2xl border-t border-white/10 dark:border-slate-700/50 animate-in slide-in-from-top duration-300 z-40 backdrop-blur-xl">
                        <div className="px-4 py-4">

                            {/* Mobile Controls */}
                            <div className="space-y-3 pt-4 border-t border-white/10">
                                {/* Theme Toggle */}
                                <button
                                    onClick={() => {
                                        setDarkMode(!darkMode);
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className="w-full flex items-center justify-between px-4 py-3.5 bg-white/10 rounded-xl hover:bg-white/15 transition-all group"
                                >
                                    <div className="flex items-center gap-3">
                                        <FontAwesomeIcon
                                            icon={darkMode ? faSun : faMoon}
                                            className="text-white/70 group-hover:text-white"
                                        />
                                        <span className="text-white font-bold text-xs uppercase tracking-wider">
                                            {darkMode ? "Light Mode" : "Dark Mode"}
                                        </span>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={faChevronRight}
                                        className="text-white/40 text-xs"
                                    />
                                </button>

                                <div className="">
                                    <div className="flex items-center justify-center">
                                        <Switcher />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Content Area */}
            <div className="container mx-auto px-3 sm:px-4 md:px-6 py-4 md:py-8 flex-grow">
                <div className="md:bg-white md:dark:bg-slate-800/50 md:rounded-2xl p-0 md:p-8 md:shadow-lg md:border md:border-slate-100 md:dark:border-slate-700/50 md:backdrop-blur-sm">
                    {activeTab === 'DASHBOARD' && <HomeData NavigateToWallet={NavigateToWallet} />}
                    {activeTab === 'SETTINGS' && <SettingsLayout activeTabExit={resetToHome} defaultTab={settingsTab} />}
                </div>
            </div>

            {/* Modern Footer */}
            <footer className="mt-auto py-6 px-4 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-200 dark:border-slate-700/50">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">
                            © {new Date().getFullYear()} Powered by Segments. All rights reserved.
                          </p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default WorkerData;